import courseService from 'ApiService/HeadHR/headhrCourseService';
import headhrStudentService from 'ApiService/HeadHR/headhrStudentService';
import branchService from 'ApiService/SuperAdmin/branchService';

import Card from 'components/card';
import { AuthContext } from 'contexts/AuthContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { UseContextProvider } from 'views/admin/main/users/newUser/contexts/StepperContext';

import SelectClass from './Class/Index';
import AttendancesSheet from './Sheet/Index';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import PlacementReadySearch from 'pages/Common/PlacementReadySearch';

const HeadHRMarkPlacementReady = () => {
  /* ================= STEP ================= */
  const [currentStep, setCurrentStep] = useState(1);

  /* ================= CLASS DATA ================= */
  const [classData, setClassData] = useState({
    courseId: '',
    batchStructureId: '',
    batchId: '',
  });

  /* ================= DATA ================= */
  const [courses, setCourses] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [searchResults, setSearchResults] = useState([]);

  /* ================= UI ================= */
  const [disable, setDisable] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  /* ================= BRANCHES ================= */
  const [branches, setBranches] = useState([]);

  /* ================= AUTH ================= */
  const { state, loading, dispatch } = useContext(AuthContext);

  const user = state?.user;
  const hrRole = user?.hrRole;
  const allPermission = user?.allPermission;
  const userBranches = user?.branches;

  const isHeadHR = hrRole === 'headhr';
  const isJrHR = hrRole === 'jrhr';
  const hasAccessToAllBranches = isHeadHR || allPermission === true;

  const selectedBranch = state?.selectedBranch;

  /* ================= RESET ================= */
  const resetPage = () => {
    setClassData({
      courseId: '',
      batchStructureId: '',
      batchId: '',
    });
    setBatchStructures([]);
    setAttendance([]);
    setDisable(true);
    setCurrentStep(1);
  };

  /* ================= FETCH BRANCHES ================= */
  const fetchBranches = async () => {
    try {
      setLoadingBranches(true);

      if (hasAccessToAllBranches) {
        const response = await branchService.allBranches();
        if (response.data.success) {
          setBranches(response.data.allBranches || []);
        }
      } else {
        setBranches(userBranches || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoadingBranches(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    fetchBranches();
  }, [loading, user]);

  useEffect(() => {
    if (
      !hasAccessToAllBranches &&
      userBranches &&
      userBranches.length === 1 &&
      !selectedBranch
    ) {
      dispatch({ type: 'SET_SELECTED_BRANCH', payload: userBranches[0]._id });
    }
  }, [
    branches,
    hasAccessToAllBranches,
    userBranches,
    selectedBranch,
    dispatch,
  ]);

  const handleBranchChange = (event) => {
    const selectedBranchId = event.target.value;
    dispatch({ type: 'SET_SELECTED_BRANCH', payload: selectedBranchId });
    resetPage();
  };

  /* ================= RESET ON BRANCH CHANGE ================= */
  useEffect(() => {
    if (!isHeadHR && !isJrHR) return;
    if (!selectedBranch) return;

    // Reset class data when branch changes, but keep the branch selection
    setClassData({
      courseId: '',
      batchStructureId: '',
      batchId: '',
    });
    setBatchStructures([]);
    setAttendance([]);
    setDisable(true);
    setCurrentStep(1);
  }, [selectedBranch, isHeadHR, isJrHR]);

  /* ================= FETCH COURSES ================= */
  const fetchCourses = async () => {
    if (!selectedBranch) {
      setCourses([]);
      return;
    }

    try {
      setLoadingCourses(true);
      const res = await courseService.getAllCourses(selectedBranch);
      setCourses(res?.data?.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to fetch courses');
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  /* ================= FETCH COURSES ON MOUNT AND BRANCH CHANGE ================= */
  useEffect(() => {
    if (loading) return;
    if (!user) return;

    // For HeadHR/JRHR, need a branch selected
    if ((isHeadHR || isJrHR) && !selectedBranch) {
      setCourses([]);
      return;
    }

    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, selectedBranch]);

  /* ================= FETCH STRUCTURES ================= */
  const fetchBatchStructures = async (courseId) => {
    try {
      const res = await courseService.getBatchStructuresByCourse(courseId);
      setBatchStructures(res?.data?.batchStructures || []);
    } catch {
      toast.error('Failed to fetch batch structures');
      setBatchStructures([]);
    }
  };

  /* ================= FETCH BATCHES ================= */
  const fetchBatchesByStructure = async (structureId) => {
    try {
      const res = await courseService.getBatchesByBatchStructure(
        structureId,
        selectedBranch
      );
      return res?.data?.batches || [];
    } catch {
      toast.error('Failed to fetch batches');
      return [];
    }
  };

  /* ================= FETCH STUDENTS ================= */
  const fetchStudents = async () => {
    try {
      setLoadingStudents(true);

      const res = await headhrStudentService.getAllStudentforPlacement(
        classData.courseId,
        classData.batchId,
        selectedBranch
      );

      const students = res?.data?.data || [];

      if (!students.length) {
        toast.info('No students found');
        setAttendance([]);
        return;
      }

      setAttendance(
        students.map((s) => ({
          id: s._id,
          name: s.name,
          placementPriority: s.placementPriority || 'Medium',
          ready: s.readyToPlace || false,
        }))
      );
    } catch {
      toast.error('Failed to fetch students');
    } finally {
      setLoadingStudents(false);
    }
  };

  /* ================= STEP HANDLERS ================= */
  const handleNext = async () => {
    if (disable) return;
    await fetchStudents();
    setCurrentStep(2);
  };

  const handleBack = () => {
    setCurrentStep(1);
    setAttendance([]);
    // Reset all selections when going back
    setClassData({
      courseId: '',
      batchStructureId: '',
      batchId: '',
    });
    setBatchStructures([]);
    setDisable(true);
  };

  /* ================= MODAL ================= */
  const confirmPlacementReady = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedStudent) return;

    try {
      const payload = {
        studentId: selectedStudent.id,
        placementPriority: selectedStudent.placementPriority,
        courseId: selectedStudent.courseId || classData.courseId,
        batchId: selectedStudent.batchId || classData.batchId,
        selectedBranch: selectedStudent.branchId || selectedBranch,
      };

      const res = await headhrStudentService.makeReadyForPlacement(payload);
      toast.success(res?.data?.message);

      setAttendance((prev) =>
        prev.map((s) =>
          s.id === selectedStudent.id ? { ...s, ready: true } : s
        )
      );
      setSearchResults((prev) =>
        prev.map((s) =>
          s.id === selectedStudent.id ? { ...s, ready: true } : s
        )
      );

      setIsModalOpen(false);
    } catch {
      toast.error('Failed to mark ready');
    }
  };

  /* ================= LOADERS ================= */
  if (loading || loadingBranches) {
    return (
      <div className="flex h-60 items-center justify-center">Loading...</div>
    );
  }

  /* ================= STEP RENDER ================= */
  const displayStep = () => {
    if (currentStep === 1) {
      return (
        <>
          {/* Universal search across all branches - no branch needed */}
          <PlacementReadySearch
            searchStudents={(term) =>
              headhrStudentService.searchStudentsForPlacement(term)
            }
            results={searchResults}
            setResults={setSearchResults}
            confirmPlacementReady={confirmPlacementReady}
          />

          {/* Branch Selector for HeadHR/JRHR with all permissions */}
          {(isHeadHR || isJrHR) && hasAccessToAllBranches && (
            <div className="mb-6 w-full rounded-lg px-3 pt-7 md:px-8">
              <div className="flex flex-col items-center">
                <label className="mb-2 text-sm font-bold text-navy-700 dark:text-white">
                  Select Branch
                </label>
                <select
                  onChange={handleBranchChange}
                  value={selectedBranch || ''}
                  className="w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm dark:!border-white/10 dark:!bg-navy-800 dark:text-white md:w-1/3"
                >
                  <option value="">Click here to Select Branch</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {!selectedBranch ? (
            <div className="flex h-40 items-center justify-center text-gray-500">
              Please select a branch to choose a course &amp; batch
            </div>
          ) : (
            <>
              <SelectClass
                classData={classData}
                setClassData={setClassData}
                courses={courses}
                loadingCourses={loadingCourses}
                batchStructures={batchStructures}
                fetchBatchStructures={fetchBatchStructures}
                fetchBatchesByStructure={fetchBatchesByStructure}
                setDisable={setDisable}
                clearAttendance={() => setAttendance([])}
              />
            </>
          )}
        </>
      );
    }

    return (
      <AttendancesSheet
        attendance={attendance}
        setAttendance={setAttendance}
        confirmPlacementReady={confirmPlacementReady}
        loading={loadingStudents}
      />
    );
  };

  return (
    <div className="mt-10 h-full w-full">
      <div className="h-[350px] w-full rounded-[20px] bg-gradient-to-br from-brand-400 to-brand-600" />

      <div className="mx-auto h-full w-5/6 md:px-3 3xl:w-7/12">
        <Card extra="-mt-[280px] w-full md:-mt-[340px] md:px-[70px] pb-3">
          <UseContextProvider>{displayStep()}</UseContextProvider>

          <div className="flex justify-end px-3 pt-4">
            {currentStep === 2 && (
              <button
                onClick={handleBack}
                className="mr-3 rounded-xl bg-gray-200 px-14 py-2"
              >
                Prev
              </button>
            )}

            {currentStep === 1 && (
              <button
                disabled={disable}
                onClick={handleNext}
                className="rounded-xl bg-brand-900 px-16 py-2 text-white disabled:opacity-60"
              >
                Next
              </button>
            )}
          </div>
        </Card>
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleSubmit}
        title="Confirm Placement Readiness"
        message={`Mark ${selectedStudent?.name} as ready for placement?`}
        confirmText="Yes"
        cancelText="Cancel"
      />
    </div>
  );
};

export default HeadHRMarkPlacementReady;
